"use client";

import { WhenAllReady } from "@nimble/react-requests/components/shared/when-all-ready";
import { ComponentType, useCallback } from "react";

import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import SaveSchoolButtonView, {
  SaveSchoolButtonViewProps,
  SaveSchoolButtonWithTextView,
} from "@/components/shared/buttons/save-school-button/save-school-button.view";
import ResponseErrorDialog from "@/components/shared/response-error-dialog/response-error-dialog";
import useSaveSchoolMutation from "@/hooks/data/use-save-school-mutation";
import useSavedSchool from "@/hooks/data/use-saved-school";
import useSignInDrawer from "@/hooks/use-sign-in-drawer";
import useStateConfig from "@/hooks/use-state-config";
import { isWalledStateConfig } from "@/lib/states-config";
import { User } from "@/lib/types/auth";

interface SaveSchoolButtonContainerProps {
  ncesId: string;
  testId?: string;
  user: User;
}

type SaveSchoolViewComponent = ComponentType<SaveSchoolButtonViewProps>;

function useIsBoardClosed(): boolean {
  return isWalledStateConfig(useStateConfig());
}

function createSaveSchoolButtonComponents(
  ViewComponent: SaveSchoolViewComponent,
) {
  function AuthenticatedInner({
    ncesId,
    testId,
    user,
    isSaved,
  }: SaveSchoolButtonContainerProps & { isSaved: boolean }) {
    const {
      mutate: saveSchool,
      isPending: isMutating,
      error: mutateError,
      reset,
    } = useSaveSchoolMutation(user.id);

    const handleClick = useCallback(() => {
      saveSchool({ ncesId, save: !isSaved });
    }, [saveSchool, ncesId, isSaved]);

    return (
      <>
        <ViewComponent
          saved={isSaved}
          loading={isMutating}
          onClick={handleClick}
          testId={testId}
        />
        {mutateError && (
          <ResponseErrorDialog error={mutateError} onClose={reset} />
        )}
      </>
    );
  }

  function Authenticated({
    ncesId,
    user,
    ...props
  }: SaveSchoolButtonContainerProps) {
    const savedQuery = useSavedSchool(user.id, ncesId);

    return (
      <WhenAllReady
        queries={[savedQuery]}
        loader={<ViewComponent saved={false} loading />}
        ErrorWrapper={() => <ViewComponent saved={false} loading />}
      >
        <AuthenticatedInner
          isSaved={savedQuery.data === true}
          ncesId={ncesId}
          user={user}
          {...props}
        />
      </WhenAllReady>
    );
  }

  function Paywall({ testId }: Omit<SaveSchoolButtonContainerProps, "user">) {
    const { openDrawer } = useSignInDrawer();

    const handleClick = useCallback(() => {
      openDrawer("1");
    }, [openDrawer]);

    return (
      <ViewComponent
        saved={false}
        loading={false}
        onClick={handleClick}
        testId={testId}
      />
    );
  }

  const Container = withAuthGate(Authenticated, {
    customLoader: <ViewComponent saved={false} loading />,
    customPaywall: (props) => <Paywall {...props} />,
  });

  return { Authenticated, Paywall, Container };
}

const {
  Authenticated: SaveSchoolButtonAuthenticated,
  Container: SaveSchoolButtonInnerContainer,
} = createSaveSchoolButtonComponents(SaveSchoolButtonView);

const {
  Authenticated: SaveSchoolButtonWithTextAuthenticated,
  Container: SaveSchoolButtonWithTextInnerContainer,
} = createSaveSchoolButtonComponents(SaveSchoolButtonWithTextView);

export { SaveSchoolButtonAuthenticated, SaveSchoolButtonWithTextAuthenticated };

export default function SaveSchoolButtonContainer({
  testId,
  ...props
}: Omit<SaveSchoolButtonContainerProps, "user">) {
  return useIsBoardClosed() ? (
    <SaveSchoolButtonView
      saved={false}
      loading={false}
      disabled
      testId={testId}
    />
  ) : (
    <SaveSchoolButtonInnerContainer testId={testId} {...props} />
  );
}

export function SaveSchoolButtonWithTextContainer({
  testId,
  ...props
}: Omit<SaveSchoolButtonContainerProps, "user">) {
  return useIsBoardClosed() ? (
    <SaveSchoolButtonWithTextView
      saved={false}
      loading={false}
      disabled
      testId={testId}
    />
  ) : (
    <SaveSchoolButtonWithTextInnerContainer testId={testId} {...props} />
  );
}
